import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TextField,
  InputAdornment,
  Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LeaveBarChart from './LeaveBarChart';
import LeaveTypePieChart from './LeaveTypePieChart';
import LeaveDataTable from './LeaveDataTable';
import type { LeaveDataTableColumn } from './LeaveDataTable';
import { filterLeaveReportEntries } from '../utils/leaveReportDateRange';

export interface LeaveReportEntry {
  employeeId: string;
  employeeName: string;
  employeeNumber?: number;
  departmentName?: string;
  businessUnitName?: string;
  leaveTypeName: string;
  daysTaken: number;
  periodYear: number;
  /** Start of this occurrence (approved request); null for imported balance. */
  fromDate?: string | null;
  /** End of this occurrence (approved request); null for imported balance. */
  toDate?: string | null;
  /** When set (e.g. "Oct 2025"), shown in Period column instead of date range. */
  periodDescription?: string | null;
  /** 0 = approved request (sorts first), 1 = imported (sorts after). */
  periodSortOrder?: number;
  /** Sort key: approved = fromDate, imported = first day of month. */
  periodSortKey?: string | null;
  statutoryEntitlementMet: boolean;
  /** Portion of daysTaken from the leave balance table (imported/historic). Omitted or 0 when from approved requests only. */
  importedBalanceDays?: number;
  /** Approved leave request id (for opening attached evidence, e.g. sick leave notes). */
  leaveRequestId?: string | null;
}

export interface LeaveReportViewProps {
  title: string;
  icon: React.ReactNode;
  entries: LeaveReportEntry[];
  loading: boolean;
  error: string;
  onClearError: () => void;
  periodYear: number;
  /** Omit when hidePeriodYearSelect is true (year is implied by date range). */
  onPeriodYearChange?: (year: number) => void;
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  leaveTypeFilterName: string;
  onLeaveTypeFilterNameChange: (name: string) => void;
  chartOpen: boolean;
  onChartOpenChange: (open: boolean) => void;
  /** When set, the "All leave data" table is collapsible with this state (icon on the right). Omit for always expanded. */
  tableSectionOpen?: boolean;
  onTableSectionOpenChange?: (open: boolean) => void;
  emptyMessage: string;
  tableEmptyMessage: string;
  /** When true, show department filter and department chip */
  showDepartmentFilter?: boolean;
  departments?: { id: string; name: string }[];
  departmentFilterId?: string;
  onDepartmentFilterIdChange?: (id: string) => void;
  departmentsLoading?: boolean;
  /** Optional toolbar actions (e.g. export) */
  actions?: React.ReactNode;
  /** Rendered in the filters row after period year (e.g. Team Leave “direct reports only” for managers). */
  additionalFilterControls?: React.ReactNode;
  /** Rendered directly under the page title row, before the filters card (e.g. info alerts). */
  belowHeader?: React.ReactNode;
  /** Rendered after the filters card, before loading/content (e.g. Pending approvals) */
  topSections?: React.ReactNode;
  /** Rendered after the main report content */
  extraSections?: React.ReactNode;
  /** When set, shows a back button in the header that calls this (e.g. navigate(-1)) */
  onBack?: () => void;
  /** When set, replaces the default two calendar years in the year dropdown (e.g. fiscal years). */
  yearOptions?: number[];
  /** Label for the year dropdown (default "Period year"). */
  periodYearLabel?: string;
  /** Year treated as "default" for filter chips (e.g. current fiscal year). Defaults to calendar year. */
  baselinePeriodYearForChips?: number;
  /** Optional inclusive date range filter (overlaps row period). */
  dateFilter?: {
    from: string;
    to: string;
    defaultFrom: string;
    defaultTo: string;
    onFromChange: (v: string) => void;
    onToChange: (v: string) => void;
    onReset: () => void;
  };
  /** Hide the period/fiscal year dropdown (e.g. organisation view: year implied by From date). */
  hidePeriodYearSelect?: boolean;
  /** When set, show a Notes column for sick leave rows that have an approved request id. */
  onViewSickLeaveEvidence?: (row: LeaveReportEntry) => void;
}

const defaultYears = [new Date().getFullYear(), new Date().getFullYear() - 1];

const LEAVE_TABLE_COLUMNS = ['employee', 'department', 'leaveType', 'period', 'daysTaken', 'statutory'] as const;

export default function LeaveReportView({
  title,
  icon,
  entries: baseEntries,
  loading,
  error,
  onClearError,
  periodYear,
  onPeriodYearChange = () => {},
  searchQuery,
  onSearchQueryChange,
  leaveTypeFilterName,
  onLeaveTypeFilterNameChange,
  chartOpen,
  onChartOpenChange,
  tableSectionOpen,
  onTableSectionOpenChange,
  emptyMessage,
  tableEmptyMessage,
  showDepartmentFilter = false,
  departments = [],
  departmentFilterId = '',
  onDepartmentFilterIdChange,
  departmentsLoading = false,
  actions,
  additionalFilterControls,
  belowHeader,
  topSections,
  extraSections,
  onBack,
  yearOptions,
  periodYearLabel = 'Period year',
  baselinePeriodYearForChips = new Date().getFullYear(),
  dateFilter,
  hidePeriodYearSelect = false,
  onViewSickLeaveEvidence,
}: LeaveReportViewProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const tableColumns = useMemo(() => {
    if (onViewSickLeaveEvidence) {
      return [...LEAVE_TABLE_COLUMNS, 'evidence'] as LeaveDataTableColumn[];
    }
    return [...LEAVE_TABLE_COLUMNS] as LeaveDataTableColumn[];
  }, [onViewSickLeaveEvidence]);

  const yearSelectOptions = yearOptions ?? defaultYears;

  const dateRangeIsCustom =
    !!dateFilter &&
    (dateFilter.from !== dateFilter.defaultFrom || dateFilter.to !== dateFilter.defaultTo);

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    (!hidePeriodYearSelect && periodYear !== baselinePeriodYearForChips) ||
    (showDepartmentFilter && departmentFilterId !== '') ||
    leaveTypeFilterName !== '' ||
    dateRangeIsCustom;

  const leaveTypeOptions = useMemo(() => {
    const names = new Set(baseEntries.map((e) => e.leaveTypeName).filter(Boolean));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [baseEntries]);

  const filteredForChartAndTable = useMemo(
    () =>
      filterLeaveReportEntries(baseEntries, {
        searchQuery,
        leaveTypeFilterName,
        departmentFilterId: showDepartmentFilter ? departmentFilterId : '',
        departments: showDepartmentFilter ? departments : [],
        dateFrom: dateFilter?.from,
        dateTo: dateFilter?.to,
      }),
    [
      baseEntries,
      searchQuery,
      leaveTypeFilterName,
      showDepartmentFilter,
      departmentFilterId,
      departments,
      dateFilter,
    ]
  );

  const chartEntries = useMemo(() => {
    const totalByEmployee = new Map<string, number>();
    for (const e of filteredForChartAndTable) {
      totalByEmployee.set(e.employeeId, (totalByEmployee.get(e.employeeId) ?? 0) + e.daysTaken);
    }
    return [...filteredForChartAndTable].sort((a, b) => {
      const diff = (totalByEmployee.get(b.employeeId) ?? 0) - (totalByEmployee.get(a.employeeId) ?? 0);
      return diff !== 0 ? diff : a.employeeName.localeCompare(b.employeeName);
    });
  }, [filteredForChartAndTable]);

  const chartEntriesWithLeave = useMemo(() => chartEntries.filter((e) => e.daysTaken > 0), [chartEntries]);
  const uniqueWithLeave = useMemo(() => new Set(chartEntriesWithLeave.map((e) => e.employeeId)).size, [chartEntriesWithLeave]);
  const chartHeight = Math.max(300, uniqueWithLeave * 28);

  const tableEmptyMsg =
    searchQuery.trim() || leaveTypeFilterName || (showDepartmentFilter && departmentFilterId)
      ? tableEmptyMessage
      : emptyMessage;

  const clearSearch = () => {
    onSearchQueryChange('');
    setPage(0);
  };
  const clearDepartment = () => {
    onDepartmentFilterIdChange?.('');
    setPage(0);
  };
  const clearLeaveType = () => {
    onLeaveTypeFilterNameChange('');
    setPage(0);
  };

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {onBack && (
          <IconButton onClick={onBack} sx={{ ml: -1 }} title="Back" aria-label="Back" size="small">
            <ArrowBackIcon />
          </IconButton>
        )}
        {icon}
        <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
          {title}
        </Typography>
      </Box>

      {belowHeader}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search by employee name or business unit…"
              value={searchQuery}
              onChange={(e) => {
                onSearchQueryChange(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280, flex: 1 }}
            />
            {!hidePeriodYearSelect && (
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>{periodYearLabel}</InputLabel>
                <Select
                  value={periodYear}
                  label={periodYearLabel}
                  onChange={(e) => {
                    onPeriodYearChange(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  {yearSelectOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {additionalFilterControls}
            {dateFilter && (
              <>
                <TextField
                  size="small"
                  label="From"
                  type="date"
                  value={dateFilter.from}
                  onChange={(e) => {
                    dateFilter.onFromChange(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: 150,
                    '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' },
                  }}
                />
                <TextField
                  size="small"
                  label="To"
                  type="date"
                  value={dateFilter.to}
                  onChange={(e) => {
                    dateFilter.onToChange(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    minWidth: 150,
                    '& input::-webkit-calendar-picker-indicator': { cursor: 'pointer' },
                  }}
                />
              </>
            )}
            {showDepartmentFilter && (
              <FormControl size="small" sx={{ minWidth: 200 }} disabled={departmentsLoading}>
                <InputLabel>Filter by department</InputLabel>
                <Select
                  value={departmentFilterId}
                  label="Filter by department"
                  onChange={(e) => {
                    onDepartmentFilterIdChange?.(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={loading}>
              <InputLabel shrink>Filter by leave type</InputLabel>
              <Select
                value={leaveTypeFilterName}
                label="Filter by leave type"
                displayEmpty
                renderValue={(v) => (v === '' ? 'All leave types' : v)}
                onChange={(e) => {
                  onLeaveTypeFilterNameChange(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All leave types</MenuItem>
                {leaveTypeOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {actions}
          </Box>
          {hasActiveFilters && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {searchQuery.trim() !== '' && (
                <Chip label={`Search: "${searchQuery}"`} onDelete={clearSearch} size="small" color="primary" />
              )}
              {!hidePeriodYearSelect && periodYear !== baselinePeriodYearForChips && (
                <Chip
                  label={`${periodYearLabel}: ${periodYear}`}
                  onDelete={() => onPeriodYearChange(baselinePeriodYearForChips)}
                  size="small"
                />
              )}
              {dateRangeIsCustom && dateFilter && (
                <Chip
                  label={`Dates: ${dateFilter.from} – ${dateFilter.to}`}
                  onDelete={() => {
                    dateFilter.onReset();
                    setPage(0);
                  }}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {showDepartmentFilter && departmentFilterId !== '' && (
                <Chip
                  label={`Department: ${departments.find((d) => d.id === departmentFilterId)?.name ?? departmentFilterId}`}
                  onDelete={clearDepartment}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {leaveTypeFilterName !== '' && (
                <Chip
                  label={`Leave type: ${leaveTypeFilterName}`}
                  onDelete={clearLeaveType}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {topSections}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={onClearError}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': { opacity: 0.8 },
                }}
                onClick={() => onChartOpenChange(!chartOpen)}
              >
                <Typography variant="subtitle1">
                  Leave by type
                  {hasActiveFilters ? ' — filtered' : ''}
                </Typography>
                <IconButton size="small" aria-label={chartOpen ? 'Collapse' : 'Expand'}>
                  {chartOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={chartOpen}>
                <Box sx={{ mt: 2, maxHeight: 560, overflow: 'auto' }}>
                  {chartEntries.length === 0 ? (
                    <Alert severity="info">{hasActiveFilters ? tableEmptyMessage : emptyMessage}</Alert>
                  ) : (
                    <>
                      <LeaveTypePieChart
                        entries={chartEntriesWithLeave.map((e) => ({
                          leaveTypeName: e.leaveTypeName,
                          daysTaken: e.daysTaken,
                        }))}
                      />
                      <LeaveBarChart
                        entries={chartEntriesWithLeave.map((e) => ({
                          employeeId: e.employeeId,
                          employeeName: e.employeeName,
                          leaveTypeName: e.leaveTypeName,
                          daysTaken: e.daysTaken,
                        }))}
                        height={chartHeight}
                      />
                    </>
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              {tableSectionOpen !== undefined && onTableSectionOpenChange ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { opacity: 0.8 },
                    }}
                    onClick={() => onTableSectionOpenChange(!tableSectionOpen)}
                  >
                    <Typography variant="subtitle1">
                      All leave data
                      {hasActiveFilters ? ' — filtered' : ''}
                    </Typography>
                    <IconButton size="small" aria-label={tableSectionOpen ? 'Collapse' : 'Expand'}>
                      {tableSectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                  <Collapse in={tableSectionOpen}>
                    {filteredForChartAndTable.length > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 2 }}>
                        <strong>Period</strong> shows a month (e.g. Dec 2025) for imported leave balance, or a date range for leave approved in this system. When &quot;Statutory entitlement met&quot; is Yes for Annual Leave, the employee has met their country-specific statutory target for the fiscal year. Company policy (e.g. extra days from length of service) does not affect this.
                      </Typography>
                    )}
                    <LeaveDataTable
                      entries={filteredForChartAndTable}
                      columns={tableColumns}
                      emptyMessage={tableEmptyMsg}
                      pagination={{
                        page,
                        rowsPerPage,
                        onPageChange: setPage,
                        onRowsPerPageChange: setRowsPerPage,
                        rowsPerPageOptions: [10, 25, 50, 100],
                      }}
                      statutoryHeaderTooltip="Yes when employee has met their country-specific statutory annual leave target this fiscal year."
                      onViewSickLeaveEvidence={onViewSickLeaveEvidence}
                    />
                  </Collapse>
                </>
              ) : (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    All leave data
                    {hasActiveFilters ? ' — filtered' : ''}
                  </Typography>
                  {filteredForChartAndTable.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Period</strong> shows a month (e.g. Dec 2025) for imported leave balance, or a date range for leave approved in this system. When &quot;Statutory entitlement met&quot; is Yes for Annual Leave, the employee has met their country-specific statutory target for the fiscal year. Company policy (e.g. extra days from length of service) does not affect this.
                    </Typography>
                  )}
                  <LeaveDataTable
                    entries={filteredForChartAndTable}
                    columns={tableColumns}
                    emptyMessage={tableEmptyMsg}
                    pagination={{
                      page,
                      rowsPerPage,
                      onPageChange: setPage,
                      onRowsPerPageChange: setRowsPerPage,
                      rowsPerPageOptions: [10, 25, 50, 100],
                    }}
                    statutoryHeaderTooltip="Yes when employee has met their country-specific statutory annual leave target this fiscal year."
                    onViewSickLeaveEvidence={onViewSickLeaveEvidence}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {extraSections}
    </Box>
  );
}
